import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Header,
  PageContent,
  WideContainer,
  Table,
  SpinnerDimmed,
  Alerts,
  Toggle,
} from 'components';
import { Margin } from 'styled-components-spacing';
import { connect } from 'react-redux';
import { isLoading } from 'helpers';
import { useTranslation } from 'react-i18next';
import {
  loadIpmaRegOfficeStatTableCreator,
  toggleIpmaRegOfficeCourseFrCreator,
} from 'store/actionsCreators';
import { RegRow, RegSubtotalRow, RegTotalRow, TableScroll } from './RegTable.styled';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ipma.office',
    url: '/ipma/reg-office-hub',
  },
  {
    title: 'navigation.breadcrumbs.ipma.regTable',
    url: '',
  },
];

/**
 * The kinds of registered offer. The API sends one of these per course, decided
 * the same way the CPD diplomas decide which artwork to print on
 * (CourseDiplomaKind on the back end): an exam preparation course says so on
 * itself, education and training are told apart by the course type, and a
 * course with sub-courses under it is a programme.
 *
 * Each kind is priced separately, which is why every course row names its own.
 */
const COURSE_KINDS = [
  'educationCourse',
  'educationProgramme',
  'examPreparationCourse',
  'examPreparationProgramme',
  'trainingCourse',
  'trainingProgramme',
];

/** What an unrecognised kind is counted as, matching CourseDiplomaKind::FALLBACK. */
const FALLBACK_KIND = 'trainingCourse';

/** Stands in for a breakdown with nothing in it, so the cell is never blank. */
const NOTHING = '—';

/**
 * Columns a totals row runs under before its own figures start: member
 * association through to the flat rate, which describe a course and mean
 * nothing for a group of them.
 */
const DETAIL_COLUMNS = 6;

const TOTAL_COLUMNS = 10;

// CPD hours are recorded in halves, so a whole number is shown without a
// decimal and anything else with one.
const formatHours = (hours) => {
  const value = Number(hours) || 0;

  return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

/**
 * The attendance of a course read as "8 (10), 5 (6)": ten people took it for
 * eight hours, six took it for five.
 */
const formatBreakdown = (breakdown) => {
  if (!breakdown || !breakdown.length) {
    return NOTHING;
  }

  return breakdown
    .map(({ hours, people }) => `${formatHours(hours)} (${Number(people) || 0})`)
    .join(', ');
};

/**
 * Fold one course's attendance into a running tally of people per length.
 *
 * Keyed by the number rather than the string it arrived as, so eight hours
 * written 8 and 8.0 land in the same bucket.
 */
const addToTally = (tally, breakdown) => {
  (breakdown || []).forEach(({ hours, people }) => {
    const key = Number(hours) || 0;

    tally.set(key, (tally.get(key) || 0) + (Number(people) || 0));
  });
};

/** A tally back as a list, longest course first, the way a row shows it. */
const tallyToBreakdown = (tally) =>
  [...tally.entries()]
    .map(([hours, people]) => ({ hours, people }))
    .sort((a, b) => b.hours - a.hours);

/**
 * What a set of course rows adds up to: how many of them there are, how many
 * people attended, at what lengths, for how many hours in total, and how many
 * diplomas came out of it.
 *
 * Added up from the rows on the page rather than asked of the API, so a total
 * can never disagree with the rows printed above it.
 */
const totalsOf = (rows) => {
  const tally = new Map();
  const totals = { courses: 0, participants: 0, cpdHours: 0, diplomas: 0 };

  rows.forEach((row) => {
    totals.courses += 1;
    totals.participants += Number(row.participants) || 0;
    totals.cpdHours += Number(row.cpdHours) || 0;
    totals.diplomas += Number(row.diplomas) || 0;
    addToTally(tally, row.cpdHoursBreakdown);
  });

  return { ...totals, breakdown: tallyToBreakdown(tally) };
};

const RegTable = ({ items, loading, loadStatTable, toggleCourseFr }) => {
  const { t } = useTranslation();

  useEffect(() => {
    loadStatTable();
  }, []);

  const handleToggleFlatRate = (courseId, newValue) => toggleCourseFr({ courseId, newValue });

  const kindOf = (kind) => (COURSE_KINDS.includes(kind) ? kind : FALLBACK_KIND);

  const kindLabel = (kind) => t(`page.ipma.officeTable.courseKinds.${kindOf(kind)}`);

  // Written out rather than left to i18next pluralisation, which nothing else
  // in this project uses.
  const courseCount = (count) =>
    t(
      count === 1
        ? 'page.ipma.officeTable.courseCountOne'
        : 'page.ipma.officeTable.courseCountMany',
      { count },
    );

  /**
   * The courses of each member association, in the order the API sent them
   * (member association, then REG partner, then course), each followed by what
   * that member association adds up to.
   */
  const groups = useMemo(() => {
    const rowsByMa = new Map();

    items.forEach((item) => {
      const maName = item.maName || '';

      if (!rowsByMa.has(maName)) {
        rowsByMa.set(maName, []);
      }
      rowsByMa.get(maName).push(item);
    });

    return [...rowsByMa.entries()].map(([maName, rows]) => ({
      maName,
      rows,
      totals: totalsOf(rows),
    }));
  }, [items]);

  const grandTotal = useMemo(() => totalsOf(items), [items]);

  /** The four figures a totals row carries, which every such row shows alike. */
  const renderTotals = (totals) => (
    <>
      <Table.Cell style={{ textAlign: 'center' }}>
        <Table.Label>{t('page.ipma.officeTable.participants')}:</Table.Label>
        {totals.participants}
      </Table.Cell>
      <Table.Cell>
        <Table.Label>{t('page.ipma.officeTable.hoursPerParticipant')}:</Table.Label>
        {formatBreakdown(totals.breakdown)}
      </Table.Cell>
      <Table.Cell style={{ textAlign: 'center' }}>
        <Table.Label>{t('page.ipma.officeTable.cpdHours')}:</Table.Label>
        {formatHours(totals.cpdHours)}
      </Table.Cell>
      <Table.Cell style={{ textAlign: 'center' }}>
        <Table.Label>{t('page.ipma.officeTable.diplomas')}:</Table.Label>
        {totals.diplomas}
      </Table.Cell>
    </>
  );

  return (
    <>
      <Header breadcrumbs={breadcrumbs} title={t('page.ipma.officeTable.name')} />
      <PageContent>
        <WideContainer>
          <Margin bottom={{ md: 48 }}>
            <Alerts type="ipma" />
          </Margin>
          <Margin>
            <TableScroll>
              <Table tablebreakpoint="lg">
                <Table.Head>
                  <Table.Row>
                    <Table.Heading>{t('page.ipma.officeTable.memberAssociation')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.regPartner')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.courseName')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.courseKind')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.regNumber')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.flatRate')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.participants')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.hoursPerParticipant')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.cpdHours')}</Table.Heading>
                    <Table.Heading>{t('page.ipma.officeTable.diplomas')}</Table.Heading>
                  </Table.Row>
                </Table.Head>
                <Table.Body style={{ position: 'relative', height: '80px' }}>
                  {loading ? <SpinnerDimmed /> : null}
                  {!loading && !items.length ? (
                    <Table.Row>
                      <Table.Cell colSpan={TOTAL_COLUMNS}>{t('global.noResultsFound')}</Table.Cell>
                    </Table.Row>
                  ) : null}
                  {!loading &&
                    groups.map((group) => (
                      <React.Fragment key={group.maName}>
                        {group.rows.map((item, index) => (
                          <RegRow
                            key={`${item.id}-${item.courseName}`}
                            isOdd={(index + 1) % 2 !== 0}
                          >
                            <Table.Cell style={{ fontWeight: 600, color: '#4F4E5C' }}>
                              <Table.Label>
                                {t('page.ipma.officeTable.memberAssociation')}:
                              </Table.Label>
                              {item.maName}
                            </Table.Cell>
                            <Table.Cell>
                              <Table.Label>{t('page.ipma.officeTable.regPartner')}:</Table.Label>
                              {item.partnerName}
                            </Table.Cell>
                            <Table.Cell>
                              <Table.Label>{t('page.ipma.officeTable.courseName')}:</Table.Label>
                              {item.courseName}
                            </Table.Cell>
                            <Table.Cell>
                              <Table.Label>{t('page.ipma.officeTable.courseKind')}:</Table.Label>
                              {kindLabel(item.courseKind)}
                            </Table.Cell>
                            <Table.Cell>
                              <Table.Label>{t('page.ipma.officeTable.regNumber')}:</Table.Label>
                              {item.regNumber}
                            </Table.Cell>
                            <Table.Cell style={{ textAlign: 'center' }}>
                              <Table.Label>{t('page.ipma.officeTable.flatRate')}:</Table.Label>
                              <Toggle
                                on={item.flatRate}
                                onClick={() => handleToggleFlatRate(item.id, !item.flatRate)}
                              />
                            </Table.Cell>
                            <Table.Cell style={{ textAlign: 'center' }}>
                              <Table.Label>{t('page.ipma.officeTable.participants')}:</Table.Label>
                              {item.participants}
                            </Table.Cell>
                            <Table.Cell>
                              <Table.Label>
                                {t('page.ipma.officeTable.hoursPerParticipant')}:
                              </Table.Label>
                              {formatBreakdown(item.cpdHoursBreakdown)}
                            </Table.Cell>
                            <Table.Cell style={{ textAlign: 'center' }}>
                              <Table.Label>{t('page.ipma.officeTable.cpdHours')}:</Table.Label>
                              {formatHours(item.cpdHours)}
                            </Table.Cell>
                            <Table.Cell style={{ textAlign: 'center' }}>
                              <Table.Label>{t('page.ipma.officeTable.diplomas')}:</Table.Label>
                              {item.diplomas}
                            </Table.Cell>
                          </RegRow>
                        ))}
                        <RegSubtotalRow>
                          <Table.Cell colSpan={DETAIL_COLUMNS}>
                            {t('page.ipma.officeTable.maTotal', { ma: group.maName })}
                            {' · '}
                            {courseCount(group.totals.courses)}
                          </Table.Cell>
                          {renderTotals(group.totals)}
                        </RegSubtotalRow>
                      </React.Fragment>
                    ))}
                  {!loading && items.length ? (
                    <RegTotalRow>
                      <Table.Cell colSpan={DETAIL_COLUMNS}>
                        {t('page.ipma.officeTable.allMemberAssociations')}
                        {' · '}
                        {courseCount(grandTotal.courses)}
                      </Table.Cell>
                      {renderTotals(grandTotal)}
                    </RegTotalRow>
                  ) : null}
                </Table.Body>
              </Table>
            </TableScroll>
          </Margin>
        </WideContainer>
      </PageContent>
    </>
  );
};

RegTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  loadStatTable: PropTypes.func.isRequired,
  toggleCourseFr: PropTypes.func.isRequired,
};

const mapStateToProps = ({ ipma, loading }) => ({
  loading: isLoading(loading, 'IPMA'),
  items: ipma.regOfficeTable.items,
});

const mapDispatchToProps = (dispatch) => ({
  loadStatTable: () => dispatch(loadIpmaRegOfficeStatTableCreator()),
  toggleCourseFr: (payload) => dispatch(toggleIpmaRegOfficeCourseFrCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RegTable);
